// SPEC_DEMO.md PR-5. Demo controls panel.
//
// The locked pills (Data center / Finland / AWE) advertise what the
// full product covers without exposing it. The capacity slider was
// removed when the demo switched to pre-captured fixture replay
// (demoFixture.json) — capacity is fixed at the captured run's value,
// so the slider would have implied an interactivity the demo no
// longer has.

import React from 'react';
import presets from './presets.json';
import { track } from './demoTelemetry';

function LockedChoice({ active, disabled, label, locked, onClick, title }) {
  return (
    <button
      type="button"
      className={
        'dc-pill' +
        (active ? ' is-active' : '') +
        (locked ? ' is-locked' : '') +
        (disabled ? ' is-disabled' : '')
      }
      title={locked ? 'Available in full version' : title}
      onClick={locked || disabled ? undefined : onClick}
      aria-disabled={locked || disabled}
    >
      {label}
      {locked && <span className="dc-lock-icon" aria-hidden="true">🔒</span>}
    </button>
  );
}

export default function DemoControls({ params, onChange, onRun, loading, error }) {
  function applyPreset(id) {
    const p = presets[id];
    if (!p) return;
    track('preset_selected', { preset_id: id });
    onChange({
      technology: p.technology,
      country: p.country,
      subtype: p.subtype,
      __presetMapFocus: p.map_focus, // consumed by DemoApp, stripped before fetch
    });
  }

  function setTechnology(t) {
    // Cross-field: hydrogen→PEM, data_center→colocation (matches the
    // backend's strict validation in demo.py).
    onChange({
      ...params,
      technology: t,
      subtype: t === 'hydrogen' ? 'PEM' : 'colocation',
    });
  }

  return (
    <aside className="demo-controls" aria-label="Demo screening controls">
      <div className="dc-header">
        <h2 className="dc-title">Site screener</h2>
        <button
          type="button"
          className="dc-preset-link"
          onClick={() => applyPreset('hy2gen-alternatives')}
        >
          Try: Hy2Gen alternatives →
        </button>
      </div>

      <div className="dc-field">
        <label className="dc-label">Vertical</label>
        <div className="dc-pill-row">
          <LockedChoice
            label="Hydrogen"
            active={params.technology === 'hydrogen'}
            onClick={() => setTechnology('hydrogen')}
          />
          <LockedChoice label="Data center" locked />
        </div>
      </div>

      <div className="dc-field">
        <label className="dc-label">Country</label>
        <div className="dc-pill-row">
          <LockedChoice label="Norway" active={params.country === 'NO'} />
          <LockedChoice label="Finland" locked />
        </div>
      </div>

      {params.technology === 'hydrogen' && (
        <div className="dc-field">
          <label className="dc-label">Electrolyser</label>
          <div className="dc-pill-row">
            <LockedChoice label="PEM" active={params.subtype === 'PEM'} />
            <LockedChoice label="AWE" locked />
          </div>
        </div>
      )}

      <button
        type="button"
        className="dc-run"
        disabled={loading}
        onClick={onRun}
      >
        {loading ? 'Screening…' : 'Run screening'}
      </button>

      {error && <div className="dc-error">{error}</div>}
    </aside>
  );
}
